import { Button, Box, Checkbox, CircularProgress, FormControl, FormControlLabel, FormGroup, Grid, IconButton, InputAdornment, InputLabel, OutlinedInput, TextField, Typography, Stack, Alert, useTheme } from "@mui/material";
import useTranslation from "../../hooks/useTranslation";
import cssVars from '../../styles/vars.module.scss'
import NextLink from 'next/link';
import { useEffect, useState } from "react";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { validateEmail, validatePassword } from "../../helpers/validation";
import { LOGIN } from "../../graphql/authentication/mutations";
import { useMutation } from "@apollo/client";
import { useAuthStore } from "../../stores/authStore";
import { useRouter } from "next/router";
import { CREATE_CART, MERGE_CARTS } from '../../graphql/cart/mutations'
import { useSnackbar } from "notistack";
import { useContext } from "react";
import { CustomerContext } from "../../Providers/CustomerContext";

const Login = ({ website_name }) => {

    const { t } = useTranslation()
    const router = useRouter()
    const [userData, setUserData] = useState({ email: "", password: "" })

    const [showPassword, setShowPassword] = useState(false)
    const [formCheck, setFormCheck] = useState(false)
    const { enqueueSnackbar } = useSnackbar()
    const [login, { data, loading: signInLoading, error }] = useMutation(LOGIN)
    const signIn = useAuthStore((state) => state.signIn)
    const isAuthenticated = useAuthStore((state) => state.token).length > 0
    const cartId = useAuthStore((state) => state.cartId)
    const setCartId = useAuthStore((state) => state.setCartId)
    const signOut = useAuthStore((state) => state.signOut)
    const loginDone = useAuthStore((state) => state.loginDone)
    const setLoginDone = useAuthStore((state) => state.setLoginDone)

    const [loading, setLoading] = useState(false)

    const { customerData } = useContext(CustomerContext)
    console.log(customerData.store_code);

    const handleSubmit = (e) => {
        e.preventDefault()
        setFormCheck(true)
        if (e.target.checkValidity() && validateEmail(userData.email) && (userData.password)) {
            setLoading(true)
            login({
                variables: { email: userData.email, password: userData.password }
                , onCompleted: (data) => {
                    localStorage.setItem('customer', { firstname: '', lastname: '', email: '' })
                    signIn(data.login.token)
                },
                onError: () => { setLoading(false) }
            })
        }
    }

    const theme = useTheme()

    const [createCart] = useMutation(CREATE_CART)
    const [mergeCarts] = useMutation(MERGE_CARTS)


    useEffect(() => {
        if (isAuthenticated) {
            mergeCarts({
                variables: {
                    source_cart_id: cartId,
                },
                onCompleted: (data) => {
                    if (data?.mergeCarts?.id) {
                        setCartId(data.mergeCarts.id)
                        setLoginDone(true)
                    }
                },
                onError: () => {
                    createCart({
                        onCompleted: (data) => {
                            setCartId(data.cart_id)
                            setLoginDone(true)
                        },
                        onError: (err) => {
                            enqueueSnackbar(err.message)
                            signOut()
                            setLoading(false)
                        }
                    })
                }
            })
        }
    }, [isAuthenticated])

    useEffect(() => {
        if (customerData.store_code) {
            const locale = customerData.store_code;
            if (isAuthenticated && loginDone) {
                if (router?.query?.redirect) {
                    router.replace(`/${router.query.redirect}`, undefined, { locale: locale });
                }
                else {
                    router.replace("/", undefined, { locale: locale });
                }
            }
        }
    }, [loginDone, isAuthenticated, customerData.store_code])
    
    return (
        <Grid sx={{ px: { xs: "15px", lg: 5 }, py: { xs: 0, lg: 3 } }} >
            {error ?
                <Stack sx={{ width: '100%', mt: 2 }}>
                    <Alert severity="error">{error.message}</Alert>
                </Stack>
                :
                null
            }
            <Box component="form" onSubmit={handleSubmit} autoComplete="off" sx={{ mt: 3 }} position="relative" >
                <TextField
                    sx={{ mt: 0.5 }}
                    label={t("Email or Nickname")}
                    placeholder="example@example.com"
                    fullWidth
                    className="custom-input"
                    type="email"
                    error={formCheck && !validateEmail(userData.email)}
                    onChange={(e) => { setUserData({ ...userData, email: e.target.value }) }}
                />
                <Grid display={formCheck && !validateEmail(userData.email) ? "block" : "none"} sx={{ fontSize: "14px", color: cssVars.red }} >{t("Email or Nickname is required")}</Grid>
                <FormControl className="custom-input" sx={{ mt: 3 }} fullWidth variant="outlined" error={formCheck && !validatePassword(userData.password)} onChange={(e) => { setUserData({ ...userData, password: e.target.value }) }}>
                    <InputLabel htmlFor="outlined-adornment-password">{t("Password")}</InputLabel>
                    <OutlinedInput
                        type={showPassword ? 'text' : 'password'}
                        placeholder={t("Enter your password")}
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    edge="end"
                                    onClick={() => { setShowPassword(!showPassword) }}
                                >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        }
                        label={t("Password")}
                    />
                </FormControl>
                <Grid display={formCheck && !validatePassword(userData.password) ? "block" : "none"} sx={{ fontSize: "14px", color: cssVars.red }} >
                    {!userData.password && t("Password is required")}
                    {!validatePassword(userData.password) && userData.password && t("Password must have at least 8 chars")}
                </Grid>
                <Box sx={{ mt: 3 }}>
                    <NextLink href={'/customer/forgotpassword.html'} style={{ fontWeight: 500, fontSize: 16, textDecoration: "underline" }} >{t("Forgot Password")}</NextLink>
                </Box>

                <Button
                    sx={{
                        height: "48px !important",
                        fontSize: "16px",
                        marginTop: "30px !important",
                        py: "1.4rem !important",
                        backgroundColor: `${!(loading || signInLoading) ? theme.palette.primary.main : cssVars.darkGray} ! important`,
                        color: `${cssVars.white} ! important`,
                        border: `1px solid ${!(loading || signInLoading) ? theme.palette.primary.main : cssVars.darkGray} ! important`,
                        borderRadius: `${cssVars.buttonRadius} ! important`,
                        ':hover': {
                            bgcolor: `${!(loading || signInLoading) ? theme.palette.primary.dark : cssVars.darkGray} ! important `,
                            borderColor: `${!(loading || signInLoading) ? theme.palette.primary.dark : cssVars.darkGray} ! important`,
                            color: `${cssVars.white} ! important`
                        },
                        textTransform: 'unset'
                    }}
                    fullWidth type="submit"
                >
                    {t("Sign in")}
                    {(loading || signInLoading) ? <CircularProgress sx={{ ml: 1 }} style={{ width: "15px", height: "15px", color: cssVars.white }} /> : null}
                </Button>
            </Box>
            <Grid display="flex" alignItems="center" justifyContent="space-between" sx={{ pl: 1, mt: 1 }} >
                <FormGroup>
                    <FormControlLabel
                        sx={{
                            'span:last-child': {
                                fontFamily: "inherit"
                            },
                        }}
                        control={
                            <Checkbox
                                sx={{
                                    color: `${cssVars.darkGray} ! important`,
                                    '&.Mui-checked': { color: `${theme.palette.primary.main} !important` },
                                    pl: 0,
                                    ":hover": {
                                        bgcolor: "transparent"
                                    }
                                }}
                            />
                        }
                        label={
                            <Box
                                component="span"
                                sx={{
                                    fontWeight: 400,
                                    fontSize: "14px",
                                    fontFamily: "inherit"
                                }}>
                                {t("Remember me")}
                            </Box>
                        }
                    />
                </FormGroup>
            </Grid>
        </Grid>
    );
}

export default Login;